import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import { QUERY_STRING_STORE } from '@/helpers/constants';
import MeetingRoomUsage from '@/components/communication/MeetingRoomUsage';
import { fetchData } from '@/components/communication/MeetingRoomUsage/query-request';
import PeopleCounts from '@/components/communication/PeopleCounts';
import {
  getPeopleCountsAreaByFloorId,
  fetchPeopleCountFormat,
  fetchPeopleCountThresholds,
} from '@/components/communication/PeopleCounts/query-request';
import AirQualityNow from '@/components/facility_management/AirQuality/AirQualityNow';
import {
  fetchSensorDataNow,
  fetchSensorDataThreshold,
} from '@/components/facility_management/AirQuality/AirQualityNow/query-request';
import {
  COMPONENT_CHILD_ID_GROUP,
  COMPONENT_ID_GROUP,
} from '@/helpers/constants';
import {
  calculateRectangle,
  transformCoordinateBottomLeftToTopLeft,
  generateRandomString,
} from '@/helpers/utility';
import '@egjs/flicking-plugins/dist/arrow.css';
import Flicking, { ViewportSlot } from '@egjs/react-flicking';
import '@egjs/react-flicking/dist/flicking.css';
import { format } from 'date-fns';
import { isEqual, isNil } from 'lodash';
import { Button } from 'primereact/button';
import PropTypes from 'prop-types';
import React, { Component, createRef, Fragment } from 'react';
import { connect } from 'react-redux';
import Countdown from '@/components/CustomComponent/CustomCountDown/CustomCountDown'; // Import the Countdown component
import { COMMON_TEXT } from '@/helpers/common-text';
import CustomCurrentTime from '@/components/CustomComponent/CustomCurrentTime';

@connect(state => ({
  querystring: state.querystring,
  sessionStore: state.session,
}))
class SignagePlay extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isFlickingPlay: false,
      fetchedData: {},
      isFetchingAllData: false,
      buttonsDisabled: false,
      countdownDuration: 0,
      countdownKey: generateRandomString(10),
      isPeopleFormatCount: false,
      areaLegend: [],
      peopleCountThresholds: null,
    };
    this.flickingDurationArr = [];
    this.autoPlayTimeout = null;
    this.flicking = createRef();
  }

  componentDidMount() {
    const { items } = this.props;
    this.flickingDurationArr = items.map(item => item.interval * 1000);
    this.getAllAndUpdateAllData({ items });
  }

  componentDidUpdate(prevProps, prevState) { }

  componentWillUnmount() {
    clearTimeout(this.autoplayTimeout);
    clearTimeout(this.countdownTimeout);
  }

  getDataRoomUsageByFloor = async ({ floorId }) => {
    let dataFloorMap = await fetchData({
      floorId: floorId,
    });
    if (!dataFloorMap) {
      dataFloorMap = [];
    }

    dataFloorMap = dataFloorMap
      .sort((a, b) => {
        if (a?.device_name < b?.device_name) {
          return -1;
        }
        if (a?.device_name > b?.device_name) {
          return 1;
        }
        return 0;
      })
      .map(item => ({
        ...item,
        timestamp: format(new Date(item.timestamp), 'MM月dd日 - HH:mm'),
      }));

    const updatedFloorMapData = dataFloorMap.map(item => ({
      position: {
        x: parseFloat(item?.position_x),
        y: parseFloat(item?.position_y),
      },
      data: item ?? null,
    }));

    return { updatedFloorMapData, dataFloorMap };
  };

  getDataPeopleCountsByFloor = async ({ floorId }) => {
    let dataFloorMap = await getPeopleCountsAreaByFloorId({
      floorId: floorId,
    });

    if (!dataFloorMap) {
      dataFloorMap = [];
    }

    const dataPeopleCounts = dataFloorMap.map(item => {
      const { position_x, position_y, position_x1, position_y1 } = item;
      const { pointC, width, height } = calculateRectangle({
        positionX: position_x,
        positionY: position_y,
        positionX1: position_x1,
        positionY1: position_y1,
      });
      const { x, y } = transformCoordinateBottomLeftToTopLeft({
        x: pointC.x,
        y: pointC.y,
      });
      item.rect = {
        top: y,
        left: x,
        width,
        height,
      };
      return item;
    });

    return { dataPeopleCounts };
  };

  getSensorDataByFloor = async ({ floorId }) => {
    let sensorDataRes = await fetchSensorDataNow({
      floorId: floorId,
    });

    let sensorData = [];
    if (sensorDataRes && sensorDataRes.length > 0) {
      sensorData = sensorDataRes.map(item => ({
        position: {
          x: parseFloat(item?.position_x),
          y: parseFloat(item?.position_y),
        },
        data: item ?? null,
        // For testing
        // data: {
        //   ...item ?? null,
        //   carbon_dioxide: Math.floor((Math.random() * 1000) + 1)
        // },
      }));
    }

    let sensorDataThreshold = await fetchSensorDataThreshold({
      floorId: floorId,
    });
    sensorDataThreshold = sensorDataThreshold?.[0] ?? {};
    return { sensorData, sensorDataThreshold };
  };

  getAllAndUpdateAllData = async ({ items }) => {
    this.setState({ isFetchingAllData: true });
    const newFetchedData = {};
    const fetchPromises = items.map(
      async ({ component_id, floor_id, rowId }) => {
        const { data } = await this.getDataByComponentAndFloor({
          componentId: component_id,
          floorId: floor_id,
        });
        newFetchedData[rowId] = data;
      }
    );
    await Promise.all(fetchPromises);
    const { fetchedData } = this.state;

    const isDataChanged = !isEqual(fetchedData, newFetchedData);
    this.setState(
      {
        fetchedData: isDataChanged ? newFetchedData : fetchedData,
        isFetchingAllData: false,
        isFlickingPlay: false,
      },
      () => {
        this.fetchDataByTenantId();
        // Start autoplay automatically after data is loaded
        setTimeout(() => {
          this.setState({ isFlickingPlay: true }, () => {
            this.startAutoplay();
          });
        }, 1000); // Small delay to ensure everything is ready
      }
    );
  };

  fetchDataByTenantId = async () => {
    const { querystring } = this.props;
    const tenant = querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];

    let isPeopleFormatCount = false;
    let areaLegend = [];
    const dataPeopleCountFormat = await fetchPeopleCountFormat({
      tenantId: tenant,
    });
    const peopleCountThresholds = await fetchPeopleCountThresholds({
      tenantId: tenant,
    });

    if (
      dataPeopleCountFormat &&
      dataPeopleCountFormat[0]?.people_counts_format === 'count'
    ) {
      isPeopleFormatCount = true;
    }

    let peopleCountThresholdsData = null;
    if (peopleCountThresholds && peopleCountThresholds[0]) {
      const { good, fair, bad, poor } = peopleCountThresholds[0];
      peopleCountThresholdsData = { good, fair, bad, poor };
      
      // Use % symbol only when format is percentage, not count
      const percentSymbol = isPeopleFormatCount ? '' : '%';
      
      areaLegend = [
        {
          label: COMMON_TEXT.EMPTY,
          color: '#00B573',
          value: `~ ${good - 1}${percentSymbol}`,
        },
        {
          label: '',
          color: '#ADDE61',
          value: `${good}${percentSymbol} ~ ${fair - 1}${percentSymbol}`,
        },
        {
          label: '',
          color: '#FFE135',
          value: `${fair}${percentSymbol} ~ ${bad - 1}${percentSymbol}`,
        },
        {
          label: '',
          color: '#FFA14F',
          value: `${bad}${percentSymbol} ~ ${poor - 1}${percentSymbol}`,
        },
        {
          label: COMMON_TEXT.CROWDED,
          color: '#E16A7C',
          value: `${poor}${percentSymbol} ~`,
        },
      ];
    }

    this.setState({
      isPeopleFormatCount,
      areaLegend,
      peopleCountThresholds: peopleCountThresholdsData,
    });
  };

  getAndUpdateData = async ({ componentId, floorId, rowId, itemInterval }) => {
    const { data } = await this.getDataByComponentAndFloor({
      componentId,
      floorId,
    });
    const currentDataByRowId = this.state.fetchedData[rowId] ?? [];
    if (!isEqual(currentDataByRowId, data)) {
      this.setState({
        fetchedData: {
          ...this.state.fetchedData,
          [rowId]: data,
        },
      });
    }
  };

  getDataByComponentAndFloor = async ({ componentId, floorId }) => {
    let data = [];
    switch (componentId) {
      case COMPONENT_ID_GROUP.ROOM_AVAILABILITIES:
        data = await this.getDataRoomUsageByFloor({ floorId: floorId });
        break;
      case COMPONENT_ID_GROUP.PEOPLE_COUNTS:
        data = await this.getDataPeopleCountsByFloor({ floorId: floorId });
        break;
      case COMPONENT_ID_GROUP.ENVIRONMENTS:
        data = await this.getSensorDataByFloor({ floorId: floorId });
        break;
      default:
        break;
    }
    return { data: data ?? [] };
  };

  // Flicking event listener
  flickingOnChanged = ({
    currentTarget,
    eventType,
    index,
    panel,
    prevIndex,
    prevPanel,
    isTrusted,
    direction,
  }) => {
    const { isFlickingPlay } = this.state;
    if (!isFlickingPlay) {
      return;
    }
    const { items } = this.props;
    const nextSlideIndex = (index + 1) % items.length;
    const nextSlideItem = items[nextSlideIndex];
    const { component_id, floor_id, rowId, interval } = nextSlideItem;
    this.getAndUpdateData({
      componentId: component_id,
      floorId: floor_id,
      rowId,
      itemInterval: interval,
    });

    // Restart autoplay with new interval after a short delay to ensure animation is complete
    clearTimeout(this.autoplayTimeout);
    setTimeout(() => {
      if (this.state.isFlickingPlay) {
        this.startAutoplay();
      }
    }, 100);
  };

  getCurrentFlickingIndex = () => {
    return this.flicking.current.index;
  };

  startAutoplay = () => {
    const { items } = this.props;
    const { isFlickingPlay } = this.state;

    if (!isFlickingPlay || !items || items.length === 0) {
      return;
    }

    const currentIndex = this.getCurrentFlickingIndex();
    const currentItem = items[currentIndex];
    const interval = currentItem?.interval || 5; // Default to 5 seconds if no interval set

    // Start countdown 3 seconds before slide change (but not if interval is less than 3 seconds)
    const countdownStartTime = Math.max(0, (interval - 3) * 1000);

    // Set countdown timer only if we have enough time
    if (interval >= 3) {
      this.countdownTimeout = setTimeout(() => {
        if (this.state.isFlickingPlay) {
          this.setState({
            countdownDuration: 3000, // 3 seconds in milliseconds
            countdownKey: generateRandomString(10),
          });
        }
      }, countdownStartTime);
    }

    this.autoplayTimeout = setTimeout(() => {
      if (this.state.isFlickingPlay && this.flicking.current) {
        // Clear countdown when slide changes
        this.setState({ countdownDuration: 0 });

        // Check if animation is already in progress
        if (!this.flicking.current.animating) {
          const nextIndex = (currentIndex + 1) % items.length;
          this.flicking.current.moveTo(nextIndex)
            .then(() => {
              // Only continue autoplay if still playing
              if (this.state.isFlickingPlay) {
                this.startAutoplay();
              }
            })
            .catch((error) => {
              console.warn('Autoplay moveTo failed:', error);
              // Retry after a short delay if animation was already playing
              if (this.state.isFlickingPlay) {
                setTimeout(() => this.startAutoplay(), 1000);
              }
            });
        } else {
          // If animation is in progress, retry after a short delay
          if (this.state.isFlickingPlay) {
            setTimeout(() => this.startAutoplay(), 500);
          }
        }
      }
    }, interval * 1000);
  };

  // flicking button control
  flickingButtonGroup = () => {
    const { isFlickingPlay, buttonsDisabled, countdownDuration, countdownKey } = this.state;

    const pauseTheFlicking = () => {
      if (isFlickingPlay) {
        this.setState({
          isFlickingPlay: false,
          countdownDuration: 0 // Clear countdown when paused
        });
        clearTimeout(this.autoplayTimeout);
        clearTimeout(this.countdownTimeout);
      }
    };

    const disableButtonsTemporarily = () => {
      this.setState({ buttonsDisabled: true });
      setTimeout(() => {
        this.setState({ buttonsDisabled: false });
      }, 700);
    };

    const playBtnOnClicked = async () => {
      if (isFlickingPlay) {
        pauseTheFlicking();
      } else {
        this.setState({ isFlickingPlay: true }, () => {
          this.startAutoplay();
        });
      }
      disableButtonsTemporarily();
    };

    const nextBtnOnClicked = async () => {
      // Pause autoplay before moving to the next panel
      pauseTheFlicking();

      const currentIndex = this.getCurrentFlickingIndex();
      const newIndex = (currentIndex + 1) % this.flicking.current.panelCount;
      if (!this.flicking.current.animating) {
        this.flicking.current
          .moveTo(newIndex)
          .then(() => {
            // Manual navigation successful
          })
          .catch((error) => {
            console.warn('Manual navigation failed:', error);
          });
      }
      disableButtonsTemporarily();
    };

    const prevBtnOnClicked = async () => {
      // Pause autoplay before moving to the next panel
      pauseTheFlicking();

      const currentIndex = this.getCurrentFlickingIndex();
      const totalCount = this.flicking.current.panelCount;
      const newIndex = (currentIndex - 1 + totalCount) % totalCount;
      if (!this.flicking.current.animating) {
        this.flicking.current
          .moveTo(newIndex)
          .then(() => {
            // Manual navigation successful
          })
          .catch((error) => {
            console.warn('Manual navigation failed:', error);
          });
      }
      disableButtonsTemporarily();
    };

    return (
      <div className="signage-footer">
        <div className="control-button-container">
          <Button
            text
            className="control-button control-back-btn"
            icon="pi pi-backward"
            severity="secondary"
            disabled={buttonsDisabled}
            onClick={() => prevBtnOnClicked()}
          ></Button>
          <Button
            text
            className="control-button control-play-btn"
            icon={`pi pi-${isFlickingPlay ? 'pause' : 'play'}`}
            severity="secondary"
            disabled={buttonsDisabled}
            onClick={() => playBtnOnClicked()}
          ></Button>
          <Button
            text
            className="control-button control-next-btn"
            icon="pi pi-forward"
            severity="secondary"
            disabled={buttonsDisabled}
            onClick={() => nextBtnOnClicked()}
          ></Button>
        </div>
        <Countdown duration={countdownDuration} key={countdownKey} />
      </div>
    );
  };

  renderComponent = ({
    item,
    fetchedData,
    index,
    isPeopleFormatCount,
    areaLegend,
    peopleCountThresholds,
  }) => {
    const { component_id, floor_id, component_child_id, rowId } = item;
    const componentData = fetchedData[rowId];
    const signageModeData = {
      data: componentData ?? [],
      floor: floor_id,
    };
    if (isNil(componentData)) {
      return <Fragment key={-1}></Fragment>;
    }
    switch (component_id) {
      case COMPONENT_ID_GROUP.ROOM_AVAILABILITIES:
        return (
          <MeetingRoomUsage
            key={index}
            floor={floor_id}
            isSignageMode={true}
            signageModeData={signageModeData}
            CurrentTimeComponent={CustomCurrentTime}
          />
        );
      case COMPONENT_ID_GROUP.PEOPLE_COUNTS:
        return (
          <PeopleCounts
            key={index}
            signageModeData={signageModeData}
            isSignageMode={true}
            isPeopleFormatCount={isPeopleFormatCount}
            areaLegend={areaLegend}
            peopleCountThresholds={peopleCountThresholds}
            CurrentTimeComponent={CustomCurrentTime}
          />
        );
      case COMPONENT_ID_GROUP.ENVIRONMENTS:
        switch (component_child_id) {
          case COMPONENT_CHILD_ID_GROUP.TEMPERATURE:
            return (
              <AirQualityNow
                key={index}
                signageModeData={{
                  ...signageModeData,
                  selectedSensorType: 'temperature',
                }}
                isSignageMode={true}
                CurrentTimeComponent={CustomCurrentTime}
              />
            );
          case COMPONENT_CHILD_ID_GROUP.HUMIDITY:
            return (
              <AirQualityNow
                key={index}
                signageModeData={{
                  ...signageModeData,
                  selectedSensorType: 'humidity',
                }}
                isSignageMode={true}
                CurrentTimeComponent={CustomCurrentTime}
              />
            );
          case COMPONENT_CHILD_ID_GROUP.CARBON_DIOXIDE:
            return (
              <AirQualityNow
                key={index}
                signageModeData={{
                  ...signageModeData,
                  selectedSensorType: 'carbon_dioxide',
                }}
                isSignageMode={true}
                CurrentTimeComponent={CustomCurrentTime}
              />
            );
          case COMPONENT_CHILD_ID_GROUP.NOISE_LEVEL:
            return (
              <AirQualityNow
                key={index}
                signageModeData={{
                  ...signageModeData,
                  selectedSensorType: 'noise_level',
                }}
                isSignageMode={true}
                CurrentTimeComponent={CustomCurrentTime}
              />
            );
          default:
            return <Fragment key={-2}></Fragment>;
        }
      default:
        break;
    }
    return <Fragment key={-3}></Fragment>;
  };

  flickingComponent = () => {
    const { items } = this.props;
    const { fetchedData, isPeopleFormatCount, areaLegend, peopleCountThresholds } = this.state;

    const RenderComponentMemo = React.memo(this.renderComponent);
    const FlickingButtonGroupMemo = React.memo(this.flickingButtonGroup);

    const { notice } = this.props;
    const noticeStr = notice.replace(/\\n/g, '\n');

    return (
      <Flicking
        ref={this.flicking}
        panelsPerView={1}
        duration={1000}
        align={'center'}
        moveType={['strict', { count: 1 }]}
        preventClickOnDrag={true}
        preventDefaultOnDrag={true}
        changeOnHold={true}
        renderOnlyVisible={true}
        circular={true}
        onChanged={this.flickingOnChanged}
        autoResize={true}
        useResizeObserver={true}
      >
        {items.map((item, idx) => (
          <div className="panel" key={idx}>
            <RenderComponentMemo
              key={idx}
              index={idx}
              item={item}
              fetchedData={fetchedData}
              isPeopleFormatCount={isPeopleFormatCount}
              areaLegend={areaLegend}
              peopleCountThresholds={peopleCountThresholds}
            />
          </div>
        ))}
        <ViewportSlot>
          {noticeStr && (
            <div className="notice">
              <div className="notice-content">
                <div className="notice-icon"></div>
                <div className="notice-text">{noticeStr}</div>
                {/* Hidden for compatibility with CSS */}
                <div className="title-text" style={{ display: 'none' }}>{COMMON_TEXT.TITLE_NOTICE}</div>
                <span className="config-content" style={{ display: 'none' }}>{noticeStr}</span>
              </div>
            </div>
          )}
          <FlickingButtonGroupMemo></FlickingButtonGroupMemo>
        </ViewportSlot>
      </Flicking>
    );
  };

  render() {
    const { isFetchingAllData } = this.state;
    const FlickingMemo = this.flickingComponent;

    return (
      <div className="signage-play-container">
        <div className="card">
          {isFetchingAllData ? (
            <LoadingSpinner />
          ) : (
            <>
              <FlickingMemo />
            </>
          )}
        </div>
      </div>
    );
  }
}

SignagePlay.propTypes = {
  items: PropTypes.array,
  notice: PropTypes.string,
};
export default SignagePlay;
